import axios from 'axios';
import { emitErrorNotification } from './notificationBus';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
  withCredentials: true, // Send httpOnly auth cookie
  headers: {
    'Content-Type': 'application/json',
  },
});

// No Authorization header interceptor — auth token is in httpOnly cookie.
// The browser sends it automatically with { withCredentials: true }.

const parseFriendlyApiError = (error) => {
  if (error.response) {
    const { status, data } = error.response;
    const payloadMessage = data?.message || data?.error;

    switch (status) {
      case 400:
        return payloadMessage || 'Invalid request. Please review and try again.';
      case 401:
        return payloadMessage || 'Your session expired. Please sign in again.';
      case 403:
        return payloadMessage || 'You do not have permission to perform this action.';
      case 404:
        return payloadMessage || 'Requested resource was not found.';
      case 423:
        return payloadMessage || 'Your account is temporarily locked.';
      case 429:
        return payloadMessage || 'Too many requests. Please wait and try again.';
      case 500:
        return payloadMessage || 'Server error occurred. Please try again.';
      default:
        return payloadMessage || 'Request failed. Please try again.';
    }
  }

  if (error.request) {
    return 'Network connection was lost. Please check your connection and try again.';
  }

  return error.message || 'Unexpected error occurred. Please try again.';
};

// Token is stored in httpOnly cookie — no localStorage interceptor needed.
// The browser sends it automatically with { withCredentials: true }.

// Requests where a 401 is an EXPECTED possible outcome on a public page
// (e.g. the initial "am I logged in?" probe on /login) rather than a sign
// that a previously-authenticated session was invalidated mid-use. Redirecting
// on these would bounce the browser back to the very page it's already on.
const AUTH_PROBE_PATHS = ['/auth/verify'];

const isAuthProbeRequest = (config) => {
  const url = config?.url || '';
  return AUTH_PROBE_PATHS.some((probePath) => url.includes(probePath));
};

// Public routes that never require a session. A 401 encountered while
// already on one of these must never trigger a redirect — doing so when the
// destination equals the current location forces a full page reload
// (window.location.href assignment to the current URL reloads the page),
// which would re-mount the app, re-run the auth probe, get another 401, and
// redirect again — an infinite reload loop.
const isOnPublicAuthRoute = () => {
  const path = window.location.pathname;
  return path === '/login' || path.startsWith('/unlock/');
};

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only force a redirect when a session that was actively in use just
      // got invalidated (e.g. an authenticated dashboard call started
      // failing). Never redirect for the initial auth-check probe or when
      // already sitting on a public auth route — both are expected,
      // non-error states and redirecting would create a reload loop.
      if (!isAuthProbeRequest(error.config) && !isOnPublicAuthRoute()) {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    const shouldSuppressGlobalError =
      error.config?.suppressGlobalErrorNotification ||
      error.config?.headers?.['X-Suppress-Global-Error'] === 'true';

    if (!shouldSuppressGlobalError) {
      emitErrorNotification(parseFriendlyApiError(error));
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => api.post('/auth/logout'),
  getProfile: () => api.get('/auth/profile'),
  updateProfile: (data) => api.put('/auth/profile', data),
  verifyToken: () => api.get('/auth/verify'),
  getSessions: () => api.get('/auth/sessions'),
  revokeSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
  requestUnlock: (username) => api.post('/auth/request-unlock', { username }),
  verifyUnlock: (token) => api.post(`/auth/unlock/${token}`),
};

// Analytics API
export const analyticsAPI = {
  getOverview: (params) => api.get('/analytics/overview', { params }),
  getInstallations: (params) => api.get('/analytics/installations', { params }),
  getUsage: (params) => api.get('/analytics/usage', { params }),
  getGeographic: () => api.get('/analytics/geographic'),
  getBusinessTypes: () => api.get('/analytics/business-types'),
  collectData: (data) => api.post('/analytics/collect', data),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: () => api.get('/dashboard/summary'),
  getRealtime: () => api.get('/dashboard/realtime'),
  getPerformance: (params) => api.get('/dashboard/performance', { params }),
  getFeatures: (params) => api.get('/dashboard/features', { params }),
};

// Reports API
export const reportsAPI = {
  getComprehensive: (params) => api.get('/reports/comprehensive', { params }),
  getRevenue: (params) => api.get('/reports/revenue', { params }),
  getUserBehavior: (params) => api.get('/reports/user-behavior', { params }),
  getPerformance: (params) => api.get('/reports/performance', { params }),
};

// Utility functions
export const handleApiError = (error) => {
  return parseFriendlyApiError(error);
};

// Enhanced error handler that returns full error details for rate limiting
export const handleApiErrorDetailed = (error) => {
  if (error.response) {
    const { status, data } = error.response;

    return {
      message: handleApiError(error),
      status,
      code: data.code,
      canRequestUnlock: data.canRequestUnlock || false,
      retryAfter: data.retryAfter,
      isRateLimited: status === 429
    };
  }

  return {
    message: handleApiError(error),
    status: null,
    code: null,
    canRequestUnlock: false,
    retryAfter: null,
    isRateLimited: false
  };
};

// Export default api instance
export default api;
